import { type MouseEvent } from 'react';
import { Box, Tooltip } from '@mui/material';
import { ActionDefinition } from '@sm-dashboard/types';
import {
  Button,
  Icon,
  IconButton,
  Loader,
  type IconButtonColor,
  type IconName
} from '@sm-dashboard/ui-components';

const DS_COLORS: IconButtonColor[] = ['primary', 'secondary', 'warning', 'info', 'error', 'success'];

const toDsColor = (action: ActionDefinition): IconButtonColor => {
  if (action.style) {
    const token = action.style.split('.')[0];
    if (DS_COLORS.includes(token as IconButtonColor)) {
      return token as IconButtonColor;
    }
  }
  switch (action.type) {
    case 'waze':
      return 'info';
    case 'whatsapp':
      return 'success';
    case 'delete':
      return 'error';
    case 'move':
      return 'success';
    case 'edit':
    case 'receipt':
      return 'primary';
    default:
      return 'primary';
  }
};

const typeIconName: Record<NonNullable<ActionDefinition['type']>, IconName> = {
  waze: 'waze',
  whatsapp: 'whatsApp',
  delete: 'deleteOutlined',
  move: 'groupRemoveOutlined',
  edit: 'editOutlined',
  receipt: 'receipt'
};

const getActionIcon = (action: ActionDefinition) => {
  if (action.icon) return action.icon;
  if (!action.type) return null;
  return <Icon icon={typeIconName[action.type]} color="inherit" size="small" />;
};

const getActionLabel = (action: ActionDefinition) => {
  if (action.tooltip) return action.tooltip;
  if (action.label) return action.label;
  switch (action.type) {
    case 'waze':
      return 'ניווט בוויז';
    case 'whatsapp':
      return 'פתיחה בוואטסאפ';
    case 'delete':
      return 'מחק';
    case 'move':
      return 'העברה לשליח/ה';
    case 'edit':
      return 'עריכה';
    case 'receipt':
      return 'פתח קבלה';
    default:
      return null;
  }
};

const getActionDisabled = (action: ActionDefinition, row: any, childRow?: any) => {
  if (action?.disabled?.(row, childRow)) return true;
  switch (action.type) {
    case 'waze':
      return !hasValidLocation(row);
    case 'whatsapp':
      return !hasPhone(row);
    default:
      return false;
  }
};

const hasValidLocation = (row: any) => {
  if (row?.address) {
    return true;
  }
  if (!row?.geom?.coordinates) return false;
  if (Array.isArray(row?.geom?.coordinates)) {
    return row?.geom?.coordinates.length === 2;
  }
  if (typeof row?.geom?.coordinates === 'object') {
    return row?.geom?.coordinates.lat && row?.geom?.coordinates.lng;
  }
  return false;
};
const hasPhone = (row: any) => {
  return row?.phone && row?.phone?.length > 0;
};

const openWaze = (row: any) => {
  let wazeUrl = 'https://waze.com/ul';
  if (
    row?.geom?.coordinates &&
    ((Array.isArray(row?.geom?.coordinates) && row?.geom?.coordinates.length === 2) ||
      (typeof row?.geom?.coordinates === 'object' && row?.geom?.coordinates.lat && row?.geom?.coordinates.lng))
  ) {
    wazeUrl += `?ll=${row?.geom?.coordinates[1]},${row?.geom?.coordinates[0]}&navigate=yes`;
    window.open(wazeUrl, '_blank');
  }
  if (row?.address) {
    wazeUrl += `?q=${encodeURIComponent(row?.address)}&navigate=yes`;
    window.open(wazeUrl, '_blank');
  }
  window.open(wazeUrl, '_blank');
};
const openWhatsApp = (row: any) => {
  const whatsappUrl = `https://wa.me/${row.phone?.replace('0', '972')}`;
  window.open(whatsappUrl, '_blank');
};

const GridActionIconButton = ({
  action,
  disabled,
  loading,
  actionLabel,
  onClick
}: {
  action: ActionDefinition;
  disabled: boolean;
  loading?: boolean;
  actionLabel?: string;
  onClick: (event: MouseEvent<HTMLButtonElement>) => void;
}) => {
  const button = (
    <IconButton
      variant="outlined"
      color={toDsColor(action)}
      shape="circle"
      aria-label={actionLabel}
      onClick={onClick}
      disabled={disabled}
    >
      {loading ? <Loader size={18} color="inherit" /> : getActionIcon(action)}
    </IconButton>
  );

  return (
    <Tooltip title={actionLabel} placement="right">
      {disabled ? <Box component="span">{button}</Box> : button}
    </Tooltip>
  );
};

export const actionsRender = <T, U>(actions: ActionDefinition<T>[], row: T, childRow?: U) => {
  const renderedActions = actions?.map((action, index) => {
    const { show, tooltip, label, onClick, loading, renderCell, level } = action;
    if (show?.(row, childRow) === false) return null;
    if (renderCell) return renderCell(row, childRow);

    const color = toDsColor(action);
    const isLoading = typeof loading === 'function' ? loading(row, childRow) : loading;
    const disabled = getActionDisabled(action, row, childRow) || Boolean(isLoading);
    const actionLabel = getActionLabel(action) ?? undefined;

    if (level === 'primary') {
      const primaryButton = (
        <Button
          variant="outlined"
          color={color}
          size="small"
          loading={isLoading}
          onClick={e => {
            e.preventDefault();
            onClick?.(row, childRow);
          }}
          disabled={disabled}
          endIcon={getActionIcon(action)}
        >
          {getActionLabel(action)}
        </Button>
      );

      return (
        <Tooltip key={index} title={tooltip || label} placement="right">
          {disabled ? <Box component="span">{primaryButton}</Box> : primaryButton}
        </Tooltip>
      );
    }

    return (
      <GridActionIconButton
        key={index}
        action={action}
        disabled={disabled}
        loading={isLoading}
        actionLabel={actionLabel}
        onClick={e => {
          e.preventDefault();
          onClick?.(row, childRow);
          if (!onClick) {
            if (action.type === 'waze') {
              openWaze(row);
            } else if (action.type === 'whatsapp') {
              openWhatsApp(row);
            }
          }
        }}
      />
    );
  });
  return renderedActions;
};

export default actionsRender;
